/**
 * Everything blocking this workshop, in one call (#2488).
 *
 * The rules themselves stay in `workshopReadiness.ts` as a pure function so
 * they can be tested without a query client and cannot drift from the server
 * checks they mirror. This hook only gathers what that function cannot reach:
 * services, lifts and mechanics are separate queries, not settings fields.
 *
 * Counts are passed through as `undefined` while loading rather than zero.
 * The difference matters — a page mid-fetch must not tell a workshop it has
 * no mechanics.
 */

import { useMemo } from 'react';
import type { WorkshopSettings } from '@/types';
import { useServices } from '@/hooks/useServices';
import { useAllLifts } from '@/hooks/useLifts';
import { useMechanics } from '@/hooks/useStaffUsers';
import {
  hasBlockingIssues,
  workshopReadinessIssues,
  type ReadinessCounts,
  type ReadinessIssue,
} from './workshopReadiness';

export interface WorkshopReadiness {
  issues: ReadinessIssue[];
  /** True when something stops a document being issued or work being done. */
  blocking: boolean;
  /** Counts are still arriving; the caller may want to stay quiet until done. */
  isLoading: boolean;
}

export function useWorkshopReadiness(settings: WorkshopSettings): WorkshopReadiness {
  const services = useServices({ isActive: true });
  const lifts = useAllLifts();
  const mechanics = useMechanics();

  const isLoading = services.isLoading || lifts.isLoading || mechanics.isLoading;

  const counts: ReadinessCounts = {
    services: services.data?.length,
    lifts: lifts.data?.length,
    mechanics: mechanics.data?.length,
  };

  const issues = useMemo(
    () => workshopReadinessIssues(settings, counts),
    // The counts object is rebuilt each render; depend on the values instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settings, counts.services, counts.lifts, counts.mechanics],
  );

  return { issues, blocking: hasBlockingIssues(issues), isLoading };
}
